import type { ItineraryDay, ItineraryEditor, ItineraryNode, PointRecommendationAnchor, TimeSlot } from "../types";
import { hasExplicitProfessionalRoutes, resolveDayEndpointNodes } from "./itineraryEditorService";
import { getItineraryProfessionalSettings } from "./itineraryProfessionalService";

/** 相对目标与实际时段决定候选推荐的回溯起点。 */
export type RecommendationPosition = {
  dayId: string;
  timeSlot: TimeSlot;
  beforeNodeId?: string;
  afterNodeId?: string;
  majorId?: string | null;
  dayEnd?: boolean;
};

const placeholderStays = new Set(["无", "火车上", "火车上住宿", "火车上住宿住宿", "无住宿", "无住宿住宿"]);
const slotOrder: Record<TimeSlot, number> = { morning: 0, afternoon: 1, evening: 2 };

/** 住宿选择可以是占位，但只有真实地点能作为推荐依据。 */
function stayAnchor(id: string | null | undefined, name: string | null | undefined): PointRecommendationAnchor | null {
  return id?.trim() && !placeholderStays.has(name?.trim() ?? "") ? { refType: "stay_area", refId: id } : null;
}

/** 仅接纳有主数据引用的考察点和住宿区域。 */
function nodeAnchor(node: ItineraryNode): PointRecommendationAnchor | null {
  if (node.nodeType === "point" && node.pointSpuId?.trim()) return { refType: "point_spu", refId: node.pointSpuId };
  if (node.nodeType === "stay_area" || String(node.nodeType) === "stay") return stayAnchor(node.stayAreaId, node.title);
  return null;
}

/** 沿所选专业路径读取节点，普通日复用编辑器顺序。 */
function dayPath(editor: ItineraryEditor, day: ItineraryDay, majorId?: string | null): ItineraryNode[] {
  const tracks = day.parallelTracks?.tracks;
  if (tracks?.length) {
    const selectedMajor = majorId || getItineraryProfessionalSettings(editor)?.mainMajorId;
    const track = tracks.find((item) => item.majorId === selectedMajor)
      ?? tracks.find((item) => item.majorId === getItineraryProfessionalSettings(editor)?.mainMajorId)
      ?? (tracks.length === 1 ? tracks[0] : undefined);
    if (!track) return [];
    const byId = new Map(day.nodes.map((node) => [node.id, node]));
    return track.nodeIds.map((id) => byId.get(id)).filter((node): node is ItineraryNode => Boolean(node));
  }
  // 旧分叉图没有路径归属时不能按数组顺序串联其他专业。
  return hasExplicitProfessionalRoutes(day, editor.segments) ? [] : resolveDayEndpointNodes(day);
}

/** 从最新草稿的实际插入位置向前寻找有效参考资源。 */
export function resolvePointRecommendationAnchor(
  editor: ItineraryEditor,
  position: RecommendationPosition,
): PointRecommendationAnchor | null {
  const current = editor.days.find((day) => day.id === position.dayId);
  if (!current) return null;
  const days = editor.days.filter((day) => day.dayNumber <= current.dayNumber)
    .sort((a, b) => b.dayNumber - a.dayNumber);
  for (const day of days) {
    let path = dayPath(editor, day, position.majorId);
    if (day.id === current.id) {
      const relativeId = position.beforeNodeId ?? position.afterNodeId;
      if (relativeId) {
        const index = path.findIndex((node) => node.id === relativeId);
        if (index < 0) return null;
        path = path.slice(0, index + (position.afterNodeId ? 1 : 0));
      } else if (!position.dayEnd) {
        path = path.filter((node) => slotOrder[node.timeSlot] <= slotOrder[position.timeSlot]);
      }
    } else {
      // 仅兼容没有住宿节点的历史日；节点存在时以草稿路径事实为准。
      const hasStayNode = day.nodes.some((node) => node.nodeType === "stay_area" || String(node.nodeType) === "stay");
      const stay = !hasStayNode ? stayAnchor(day.stayAreaId, day.stayAreaName) : null;
      if (stay) return stay;
    }
    for (let index = path.length - 1; index >= 0; index -= 1) {
      const anchor = nodeAnchor(path[index]);
      if (anchor) return anchor;
    }
  }
  return null;
}
